import { Helmet } from 'react-helmet-async';
import { transformImageUrl } from '../utils/imageTransform';

// Site configuration
const SITE_CONFIG = {
  url: 'https://www.taxiubr.com',
  name: 'TaxiUbr Thailand',
  alternateName: 'TaxiUbr',
  phone: '095-221-5764',
  phoneInternational: '+66952215764',
  line: 'https://line.me/R/ti/p/@sanookcar',
  facebook: 'https://www.facebook.com/profile.php?id=61556482434476',
  address: {
    street: '281 58 หมู่ที่ 17 ถ. มิตรภาพ ตำบลในเมือง',
    locality: 'อำเภอเมืองขอนแก่น',
    region: 'ขอนแก่น',
    postalCode: '40000',
    country: 'TH'
  },
  geo: {
    latitude: '16.4419',
    longitude: '102.8350'
  },
  priceRange: '$$',
  openingHours: '00:00-23:59'
};

// Standardize phone format to XXX-XXX-XXXX
const standardizePhone = (phone) => {
  if (!phone) return SITE_CONFIG.phone;
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  // Format as XXX-XXX-XXXX if it's 10 digits
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
};

// Transform image URL for structured data (already transformed URLs will pass through)
const transformImageForStructuredData = (imgPath, defaultPath = '/logo-black-circle.png') => {
  // If no image path provided, use default
  if (!imgPath) {
    const transformedPath = transformImageUrl(defaultPath, '');
    return transformedPath.startsWith('http') ? transformedPath : `${SITE_CONFIG.url}${transformedPath}`;
  }
  
  // If image is already a full URL, use as-is
  if (imgPath.startsWith('http://') || imgPath.startsWith('https://')) {
    return imgPath;
  }
  
  // Normalize the image path
  const normalizedPath = transformImageUrl(imgPath, '');
  // Ensure full URL (transformImageUrl returns relative paths)
  return normalizedPath.startsWith('http') ? normalizedPath : `${SITE_CONFIG.url}${normalizedPath}`;
};

const StructuredData = ({ type, data }) => {
  const getStructuredData = () => {
    switch (type) {
      case 'LocalBusiness':
        const address = {
          "@type": "PostalAddress",
          "addressLocality": data.addressLocality || SITE_CONFIG.address.locality,
          "addressRegion": data.addressRegion || SITE_CONFIG.address.region,
          "addressCountry": data.addressCountry || SITE_CONFIG.address.country,
          "postalCode": data.postalCode || SITE_CONFIG.address.postalCode
        };
        if (data.streetAddress || SITE_CONFIG.address.street) {
          address.streetAddress = data.streetAddress || SITE_CONFIG.address.street;
        }

        const sameAs = [];
        if (data.facebook) sameAs.push(data.facebook);
        if (data.line) sameAs.push(data.line);
        // Add default social links if none provided
        if (sameAs.length === 0) {
          sameAs.push(SITE_CONFIG.facebook, SITE_CONFIG.line);
        }

        const localBusiness = {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": data.name || SITE_CONFIG.name,
          "image": transformImageForStructuredData(data.image),
          "description": data.description || "บริการรถเช่าพร้อมคนขับ แท็กซี่มิเตอร์ และเหมารถสำหรับการเดินทางในขอนแก่นและทั่วประเทศไทย ตลอด 24 ชั่วโมง",
          "address": address,
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": data.latitude || SITE_CONFIG.geo.latitude,
            "longitude": data.longitude || SITE_CONFIG.geo.longitude
          },
          "url": data.url || SITE_CONFIG.url,
          "telephone": standardizePhone(data.telephone),
          "priceRange": data.priceRange || SITE_CONFIG.priceRange,
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
              "Sunday"
            ],
            "opens": data.openingHours ? (data.openingHours.includes('-') ? data.openingHours.split('-')[0].trim() : "00:00") : "00:00",
            "closes": data.openingHours ? (data.openingHours.includes('-') ? data.openingHours.split('-')[1].trim() : "23:59") : "23:59"
          },
          "sameAs": sameAs,
          "currenciesAccepted": "THB",
          "paymentAccepted": "Cash, Bank Transfer",
          "hasMap": `https://www.google.com/maps?q=${SITE_CONFIG.geo.latitude},${SITE_CONFIG.geo.longitude}`
        };

        // Add areaServed
        if (data.areaServed) {
          if (Array.isArray(data.areaServed)) {
            localBusiness.areaServed = data.areaServed.map(area => ({
              "@type": "City",
              "name": area
            }));
          } else {
            localBusiness.areaServed = {
              "@type": "City",
              "name": data.areaServed
            };
          }
        } else {
          // Default area served
          localBusiness.areaServed = [
            { "@type": "City", "name": "ขอนแก่น" },
            { "@type": "Country", "name": "ประเทศไทย" }
          ];
        }

        // Add serviceType
        if (data.serviceType) {
          localBusiness.serviceType = data.serviceType;
        }

        // Add aggregateRating if provided
        if (data.aggregateRating) {
          localBusiness.aggregateRating = {
            "@type": "AggregateRating",
            "ratingValue": data.aggregateRating.ratingValue || "4.8",
            "reviewCount": data.aggregateRating.reviewCount || "150",
            "bestRating": data.aggregateRating.bestRating || "5",
            "worstRating": data.aggregateRating.worstRating || "1"
          };
        }

        // Add reviews if provided
        if (data.review && Array.isArray(data.review) && data.review.length > 0) {
          localBusiness.review = data.review;
        }

        return localBusiness;
      
      case 'Organization':
        const orgSameAs = [];
        if (data.facebook) orgSameAs.push(data.facebook);
        if (data.line) orgSameAs.push(data.line);
        if (orgSameAs.length === 0) {
          orgSameAs.push(SITE_CONFIG.facebook, SITE_CONFIG.line);
        }

        const orgSchema = {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": data.name || SITE_CONFIG.name,
          "alternateName": data.alternateName || SITE_CONFIG.alternateName,
          "url": data.url || SITE_CONFIG.url,
          "logo": {
            "@type": "ImageObject",
            "url": transformImageForStructuredData(data.logo),
            "width": "512",
            "height": "512"
          },
          "description": data.description || "บริการแท็กซี่มิเตอร์และเหมารถพร้อมคนขับทั่วประเทศไทย ตลอด 24 ชั่วโมง รับส่งสนามบิน เหมารถไปต่างจังหวัด",
          "sameAs": orgSameAs,
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "Customer Service",
            "telephone": standardizePhone(data.telephone || SITE_CONFIG.phone),
            "areaServed": ["TH", "Thailand"],
            "availableLanguage": ["Thai", "English"],
            "hoursAvailable": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
              "opens": "00:00",
              "closes": "23:59"
            }
          },
          "address": {
            "@type": "PostalAddress",
            "streetAddress": SITE_CONFIG.address.street,
            "addressLocality": SITE_CONFIG.address.locality,
            "addressRegion": SITE_CONFIG.address.region,
            "postalCode": SITE_CONFIG.address.postalCode,
            "addressCountry": SITE_CONFIG.address.country
          }
        };

        return orgSchema;
      
      case 'TouristAttraction':
        const touristAttraction = {
          "@context": "https://schema.org",
          "@type": "TouristAttraction",
          "name": data.name,
          "description": data.description,
          "image": transformImageForStructuredData(data.image)
        };

        // Add address if provided
        if (data.addressLocality || data.addressRegion) {
          touristAttraction.address = {
            "@type": "PostalAddress",
            "addressLocality": data.addressLocality,
            "addressRegion": data.addressRegion || "ขอนแก่น",
            "addressCountry": "TH"
          };
        }

        // Add geo if provided
        if (data.latitude && data.longitude) {
          touristAttraction.geo = {
            "@type": "GeoCoordinates",
            "latitude": data.latitude,
            "longitude": data.longitude
          };
        }

        // Add URL if provided
        if (data.url) {
          touristAttraction.url = data.url;
        }

        // Add additional properties
        if (data.touristType) {
          touristAttraction.touristType = data.touristType;
        }

        if (data.isAccessibleForFree !== undefined) {
          touristAttraction.isAccessibleForFree = data.isAccessibleForFree;
        }

        return touristAttraction;
      
      case 'FAQPage':
        // FAQPage data should already include @context and @type
        // Return as-is if it's a complete schema object
        if (data && data['@context'] && data['@type']) {
          return data;
        }
        // Otherwise, wrap it in FAQPage structure
        return {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": Array.isArray(data) ? data : (data.mainEntity || [])
        };
      
      case 'BreadcrumbList':
        // BreadcrumbList data should already include @context and @type
        // Return as-is if it's a complete schema object
        if (data && data['@context'] && data['@type']) {
          return data;
        }
        // Otherwise, wrap it in BreadcrumbList structure
        return {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": Array.isArray(data) ? data : (data.itemListElement || [])
        };
      
      case 'Article':
        // Article data should already include @context and @type
        // Return as-is if it's a complete schema object
        if (data && data['@context'] && data['@type']) {
          return data;
        }

        // Build Article schema if basic data is provided
        if (data.headline || data.title) {
          const articleSchema = {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": data.headline || data.title,
            "author": {
              "@type": "Organization",
              "name": data.authorName || SITE_CONFIG.name,
              "url": data.authorUrl || SITE_CONFIG.url
            },
            "publisher": {
              "@type": "Organization",
              "name": SITE_CONFIG.name,
              "logo": {
                "@type": "ImageObject",
                "url": transformImageForStructuredData('/logo-black-circle.png')
              }
            },
            "datePublished": data.datePublished || new Date().toISOString(),
            "dateModified": data.dateModified || new Date().toISOString()
          };

          // Add description if provided
          if (data.description) {
            articleSchema.description = data.description;
          }

          // Add image if provided
          if (data.image) {
            articleSchema.image = transformImageForStructuredData(data.image);
          }

          // Add articleBody if provided
          if (data.articleBody) {
            articleSchema.articleBody = data.articleBody;
          }

          // Add keywords if provided
          if (data.keywords) {
            articleSchema.keywords = data.keywords;
          }

          return articleSchema;
        }

        // Otherwise, return null (Article should have at least a headline)
        return null;
      
      case 'Service':
        const serviceSchema = {
          "@context": "https://schema.org",
          "@type": "Service",
          "name": data.name || "บริการแท็กซี่มิเตอร์ขอนแก่น",
          "description": data.description || "บริการแท็กซี่และรถเช่าพร้อมคนขับทั่วประเทศไทย",
          "provider": {
            "@type": "LocalBusiness",
            "name": data.providerName || SITE_CONFIG.name,
            "telephone": standardizePhone(data.providerPhone || SITE_CONFIG.phone),
            "url": data.providerUrl || SITE_CONFIG.url
          },
          "serviceType": data.serviceType || "Taxi Service",
          "url": data.url || SITE_CONFIG.url
        };

        // Add areaServed
        if (data.areaServed) {
          if (typeof data.areaServed === 'string') {
            serviceSchema.areaServed = data.areaServed;
          } else if (Array.isArray(data.areaServed)) {
            serviceSchema.areaServed = data.areaServed.join(', ');
          }
        } else {
          serviceSchema.areaServed = "Khon Kaen, Thailand";
        }

        // Add offers
        if (data.offers) {
          serviceSchema.offers = {
            "@type": "Offer",
            "priceCurrency": data.offers.priceCurrency || "THB",
            "price": data.offers.price || "35-40",
            "availability": "https://schema.org/InStock"
          };
          if (data.offers.priceRange) {
            serviceSchema.offers.priceRange = data.offers.priceRange;
          }
        } else {
          serviceSchema.offers = {
            "@type": "Offer",
            "priceCurrency": "THB",
            "price": "35-40",
            "availability": "https://schema.org/InStock"
          };
        }

        // Add category
        if (data.category) {
          serviceSchema.category = data.category;
        }

        return serviceSchema;
      
      case 'TaxiService':
        // TaxiService data should already include @context and @type
        // Return as-is if it's a complete schema object
        if (data && data['@context'] && data['@type']) {
          return data;
        }

        // Build provider information
        const provider = data.provider || {
          "@type": "LocalBusiness",
          "name": data.providerName || SITE_CONFIG.name,
          "telephone": standardizePhone(data.providerPhone || SITE_CONFIG.phone),
          "url": data.providerUrl || SITE_CONFIG.url
        };

        // Otherwise, build TaxiService schema
        const taxiService = {
          "@context": "https://schema.org",
          "@type": "TaxiService",
          "name": data.name || "บริการแท็กซี่และเหมารถ",
          "description": data.description || "บริการแท็กซี่มิเตอร์และเหมารถพร้อมคนขับทั่วประเทศไทย",
          "provider": provider,
          "url": data.url || SITE_CONFIG.url
        };

        // Add areaServed
        if (data.areaServed) {
          if (Array.isArray(data.areaServed)) {
            taxiService.areaServed = data.areaServed.map(area => ({
              "@type": "City",
              "name": area
            }));
          } else {
            taxiService.areaServed = {
              "@type": "City",
              "name": data.areaServed
            };
          }
        } else {
          taxiService.areaServed = [
            { "@type": "City", "name": "ขอนแก่น" },
            { "@type": "Country", "name": "ประเทศไทย" }
          ];
        }

        // Add serviceType
        if (data.serviceType) {
          taxiService.serviceType = data.serviceType;
        }

        // Add offers if provided
        if (data.offers) {
          taxiService.offers = {
            "@type": "Offer",
            "priceCurrency": data.offers.priceCurrency || "THB",
            "availability": "https://schema.org/InStock",
            "availabilityStarts": new Date().toISOString().split('T')[0],
            "availableDeliveryMethod": "https://schema.org/OnSitePickup"
          };

          // Add price or priceRange
          if (data.offers.price) {
            taxiService.offers.price = data.offers.price;
          }
          if (data.offers.priceRange) {
            taxiService.offers.priceRange = data.offers.priceRange;
          }
        }

        // Add broker (for taxi booking)
        taxiService.broker = {
          "@type": "LocalBusiness",
          "name": SITE_CONFIG.name,
          "telephone": standardizePhone(SITE_CONFIG.phone)
        };
        
        return taxiService;
      
      case 'Product':
        // Product schema for services as products
        const productSchema = {
          "@context": "https://schema.org",
          "@type": "Product",
          "name": data.name || "บริการแท็กซี่",
          "description": data.description || "บริการแท็กซี่และรถเช่าพร้อมคนขับ",
          "brand": {
            "@type": "Brand",
            "name": SITE_CONFIG.name
          },
          "offers": {
            "@type": "Offer",
            "priceCurrency": "THB",
            "price": data.price || "1200",
            "availability": "https://schema.org/InStock",
            "url": data.url || SITE_CONFIG.url,
            "seller": {
              "@type": "Organization",
              "name": SITE_CONFIG.name
            }
          }
        };

        // Add image if provided
        if (data.image) {
          productSchema.image = transformImageForStructuredData(data.image);
        }

        // Add aggregateRating if provided
        if (data.aggregateRating) {
          productSchema.aggregateRating = {
            "@type": "AggregateRating",
            "ratingValue": data.aggregateRating.ratingValue || "4.8",
            "reviewCount": data.aggregateRating.reviewCount || "150"
          };
        }

        return productSchema;

      case 'HowTo':
        // HowTo schema for guides and tutorials
        if (!data.name || !data.step) {
          return null;
        }

        return {
          "@context": "https://schema.org",
          "@type": "HowTo",
          "name": data.name,
          "description": data.description,
          "image": data.image ? transformImageForStructuredData(data.image) : undefined,
          "totalTime": data.totalTime || "PT30M",
          "estimatedCost": data.estimatedCost ? {
            "@type": "MonetaryAmount",
            "currency": "THB",
            "value": data.estimatedCost
          } : undefined,
          "step": Array.isArray(data.step) ? data.step.map((step, index) => ({
            "@type": "HowToStep",
            "position": index + 1,
            "name": step.name || `Step ${index + 1}`,
            "text": step.text || step,
            "url": step.url,
            "image": step.image ? transformImageForStructuredData(step.image) : undefined
          })) : []
        };

      case 'WebPage':
        // WebPage schema for individual pages
        const webPageSchema = {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": data.name || data.title,
          "description": data.description,
          "url": data.url || SITE_CONFIG.url,
          "inLanguage": "th-TH",
          "isPartOf": {
            "@type": "WebSite",
            "name": SITE_CONFIG.name,
            "url": SITE_CONFIG.url
          }
        };

        // Add breadcrumb if provided
        if (data.breadcrumb) {
          webPageSchema.breadcrumb = data.breadcrumb;
        }

        // Add primaryImageOfPage if provided
        if (data.image) {
          webPageSchema.primaryImageOfPage = {
            "@type": "ImageObject",
            "url": transformImageForStructuredData(data.image)
          };
        }

        // Add author if provided
        if (data.author) {
          webPageSchema.author = {
            "@type": "Organization",
            "name": data.author
          };
        }

        return webPageSchema;

      case 'Review':
        // Review schema
        if (!data.itemReviewed || !data.author || !data.reviewRating) {
          return null;
        }

        return {
          "@context": "https://schema.org",
          "@type": "Review",
          "itemReviewed": data.itemReviewed,
          "author": {
            "@type": "Person",
            "name": data.author
          },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": data.reviewRating.ratingValue || "5",
            "bestRating": data.reviewRating.bestRating || "5",
            "worstRating": data.reviewRating.worstRating || "1"
          },
          "reviewBody": data.reviewBody,
          "datePublished": data.datePublished || new Date().toISOString()
        };

      default:
        return null;
    }
  };

  const structuredData = getStructuredData();
  
  if (!structuredData) return null;

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default StructuredData;


